import React from "react";
import { NavigateFunction } from "react-router-dom";
import { IconRefresh } from "@tabler/icons-react";
import { Button, TabsContent, Typography } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { Loader } from "../../components/Loader";
import { useQueryClient } from "@tanstack/react-query";
import { EvalResult } from "../../services/evals";
import { PaginatedResultsView } from "./PaginatedResultsView";
import { ServerPaginationControls, type ResultsPagination } from "./ServerPaginationControls";

interface ResultsTabProps {
  resultsLoading: boolean;
  evalResults: EvalResult[] | undefined;
  expandedResultId: string | null;
  setExpandedResultId: (id: string | null) => void;
  deleteResultMutation: {
    mutate: (id: string) => void;
  };
  navigate: NavigateFunction;
  resultsPagination?: ResultsPagination | null;
  setServerPage?: (page: number) => void;
}

export const ResultsTab: React.FC<ResultsTabProps> = ({
  resultsLoading,
  evalResults,
  expandedResultId,
  setExpandedResultId,
  deleteResultMutation,
  navigate,
  resultsPagination,
  setServerPage,
}) => {
  const queryClient = useQueryClient();

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ["evalResults"] });
  };

  return (
    <TabsContent value="results" className="al-overflow-x-hidden">
      <Stack direction="row" justify="between" align="center" className="al-mb-4">
        <Typography size="sm" className="al-text-gray-600">
          {resultsPagination
            ? `Total ${resultsPagination.total} result${resultsPagination.total !== 1 ? "s" : ""}`
            : evalResults
              ? `Total ${evalResults.length} result${evalResults.length !== 1 ? "s" : ""}`
              : "No results"}
        </Typography>
        <Button onClick={handleRefresh} variant="outline" size="sm" disabled={resultsLoading}>
          <IconRefresh
            className={`al-w-4 al-h-4 al-mr-2 ${resultsLoading ? "al-animate-spin" : ""}`}
          />
          Refresh Results
        </Button>
      </Stack>
      {resultsLoading ? (
        <Loader label="Loading results..." />
      ) : evalResults && evalResults.length > 0 ? (
        <>
          <PaginatedResultsView
            evalResults={evalResults}
            expandedResultId={expandedResultId}
            setExpandedResultId={setExpandedResultId}
            deleteResultMutation={deleteResultMutation}
            navigate={navigate}
          />
          {resultsPagination && resultsPagination.totalPages > 1 && setServerPage && (
            <ServerPaginationControls pagination={resultsPagination} onPageChange={setServerPage} />
          )}
        </>
      ) : (
        <div className="al-text-center al-py-8 al-border al-border-gray-200 al-rounded-md">
          <Typography className="al-text-gray-500">No results found</Typography>
        </div>
      )}
    </TabsContent>
  );
};
